import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AtSign, Lock, ArrowRight } from 'lucide-react';
import { supabase } from '../../supabase';
import { APP_ROUTES, isAdminUser } from '../../utils/auth';
import { hashPin } from '../../utils/hashPin';
import {
  getStoredAppPath, getRouteForProfile, getStoredSessionCookie,
  setStoredDriver, setStoredProfile, setStoredProfiles,
} from '../../utils/session';
import AuthLayout, { ErrorBox, Spinner, authBtnStyle, SocialButtons } from './AuthLayout';

const WA_SUPPORT = 'https://wa.me/221769040289?text=' +
  encodeURIComponent('Bonjour Wakaana Express, j\'ai oublié mon mot de passe.');

function isEmail(val) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim()); }

/**
 * Un même numéro peut porter plusieurs profils : un marchand multi-boutique,
 * ou un livreur également gestionnaire. Postgres ne garantit aucun ordre de
 * retour, donc prendre res[0] revient à tirer au sort — et une boutique créée
 * sans PIN bloquerait la connexion alors qu'un autre profil en a un.
 * On retient donc le profil authentifiable le plus ancien.
 */
function pickLoginProfile(profiles) {
  if (!profiles?.length) return null;
  const withPin = profiles.filter((p) => p.pin_hash);
  const pool = withPin.length ? withPin : profiles;
  return [...pool].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  )[0];
}
function normalizePhone(val) {
  let v = val.trim().replace(/[\s\-().+]/g, '');
  if (v.startsWith('221') && v.length > 9) v = v.slice(3);
  return v;
}

export default function Login() {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword]     = useState('');
  const [show, setShow]             = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [ready, setReady]           = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const local = getStoredAppPath();
      if (local) { navigate(local, { replace: true }); return; }

      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      const user = data.session?.user;
      if (user) {
        if (isAdminUser(user)) { navigate(APP_ROUTES.admin, { replace: true }); return; }
        const { data: p } = await supabase.from('profiles').select('*').eq('email', user.email).eq('account_status', 'approved').maybeSingle();
        if (cancelled) return;
        if (p) { setStoredProfile(p); navigate(getRouteForProfile(p) || APP_ROUTES.home, { replace: true }); return; }

        // Connexion via Google / Apple : provisionnement auto d'un compte particulier
        const provider = user.app_metadata?.provider;
        if (provider === 'google' || provider === 'apple') {
          const { data: existing } = await supabase.from('profiles').select('*').eq('email', user.email).maybeSingle();
          if (cancelled) return;
          if (existing) {
            if (existing.account_status === 'approved') { setStoredProfile(existing); navigate(getRouteForProfile(existing) || APP_ROUTES.particulier, { replace: true }); return; }
          } else {
            const fullName = user.user_metadata?.full_name || user.user_metadata?.name || '';
            const { data: prof } = await supabase.from('profiles').insert({
              name: fullName, email: user.email, role: 'particulier', account_status: 'approved',
            }).select().single();
            if (cancelled) return;
            if (prof) { setStoredProfile(prof); navigate(APP_ROUTES.particulier, { replace: true }); return; }
          }
        }
      }

      const snap = getStoredSessionCookie();
      if (snap?.id) {
        const { data: p } = await supabase.from('profiles').select('*, drivers(*)').eq('id', snap.id).maybeSingle();
        if (cancelled) return;
        if (p && p.account_status !== 'rejected') {
          setStoredProfile(p);
          if (p.role === 'driver' && p.drivers) setStoredDriver(p.drivers);
          const r = getRouteForProfile(p);
          if (r) { navigate(r, { replace: true }); return; }
        }
      }
      setReady(true);
    })().catch(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, [navigate]);

  const submit = async () => {
    const val = identifier.trim();
    if (!val || !password) return;
    setError(''); setLoading(true);

    if (isEmail(val)) {
      const { data, error: err } = await supabase.auth.signInWithPassword({ email: val.toLowerCase(), password });
      if (err) { setError('Identifiant ou mot de passe incorrect.'); setLoading(false); return; }
      const u = data.user;
      if (isAdminUser(u)) { navigate(APP_ROUTES.admin, { replace: true }); return; }
      const { data: p } = await supabase.from('profiles').select('*').eq('email', u.email).eq('account_status', 'approved').maybeSingle();
      if (!p) { await supabase.auth.signOut(); setError('Accès non autorisé.'); setLoading(false); return; }
      setStoredProfile(p); navigate(getRouteForProfile(p), { replace: true });
    } else {
      const n = normalizePhone(val);
      const { data: res } = await supabase.from('profiles').select('*, drivers(*)').or(`phone.eq.${n},phone.eq.${val.trim()}`);
      const p = pickLoginProfile(res);
      if (!p) { setError('Numéro non reconnu.'); setLoading(false); return; }
      if (p.account_status === 'pending') { setError('Compte en attente de validation.'); setLoading(false); return; }
      if (p.account_status === 'rejected') { setError('Accès refusé.'); setLoading(false); return; }
      if (!p.pin_hash) { setError('Aucun mot de passe défini.'); setLoading(false); return; }
      const h = await hashPin(password);
      if (h !== p.pin_hash) { setError('Identifiant ou mot de passe incorrect.'); setLoading(false); return; }
      // Toute la fratrie du numéro, sinon le sélecteur "Changer de compte"
      // reste vide et les autres boutiques deviennent inatteignables.
      setStoredProfiles(res);
      setStoredProfile(p);
      if (p.role === 'driver') { setStoredDriver(p.drivers); navigate(APP_ROUTES.driver, { replace: true }); }
      else { navigate(getRouteForProfile(p), { replace: true }); }
    }
    setLoading(false);
  };

  const handleOAuth = async (provider) => {
    setError('');
    const { error: e } = await supabase.auth.signInWithOAuth({
      provider, options: { redirectTo: `${window.location.origin}/login` },
    });
    if (e) setError(`Connexion ${provider === 'google' ? 'Google' : 'Apple'} indisponible pour le moment.`);
  };

  const ok = !loading && identifier.trim().length > 0 && password.length > 0;

  return (
    <AuthLayout footer="Wakaana Express · Espace équipe">
      <h2 className="auth-title">Connexion</h2>
      <p className="auth-subtitle">Connectez-vous à votre espace Wakaana Express.</p>

      {/* Identifiant */}
      <div className="auth-field">
        <span className="auth-fieldicon"><AtSign size={17} /></span>
        <input
          className={`auth-input with-icon${error ? ' err' : ''}`}
          type="text"
          value={identifier}
          onChange={e => { setIdentifier(e.target.value); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder="Email ou téléphone"
          autoFocus
          autoComplete="username"
        />
      </div>

      {/* Mot de passe */}
      <div className="auth-field">
        <span className="auth-fieldicon"><Lock size={17} /></span>
        <input
          className={`auth-input with-icon with-eye${error ? ' err' : ''}`}
          type={show ? 'text' : 'password'}
          value={password}
          onChange={e => { setPassword(e.target.value); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder="Mot de passe"
          autoComplete="current-password"
        />
        <button className="auth-eye" onClick={() => setShow(v => !v)} tabIndex={-1}>
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>

      <button className="auth-forgot" onClick={() => window.open(WA_SUPPORT, '_blank')}>
        Mot de passe oublié ?
      </button>

      <ErrorBox error={error} />

      {ready ? (
        <>
          <button className="auth-btn" onClick={submit} disabled={!ok} style={authBtnStyle(ok)}>
            {loading ? <Spinner size={18} light /> : <>Continuer <ArrowRight size={17} /></>}
          </button>

          <div className="auth-divider"><span>Pas encore de compte ?</span></div>

          <button className="auth-secondary" onClick={() => navigate(APP_ROUTES.register)}>
            Créer un compte
          </button>

          <div style={{ height: 12 }} />
          <SocialButtons
            onApple={() => handleOAuth('apple')}
            onGoogle={() => handleOAuth('google')}
          />

          <p className="auth-foot">
            Suivre un colis ?{' '}
            <button className="auth-foot-link" onClick={() => navigate(APP_ROUTES.clientLogin)}>
              Accès client
            </button>
          </p>
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '18px 0' }}>
          <Spinner size={24} />
        </div>
      )}

      <p className="auth-terms">
        En continuant, vous acceptez nos{' '}
        <span style={{ color: '#64748b', fontWeight: 600 }}>Conditions d'utilisation</span> et notre{' '}
        <span style={{ color: '#64748b', fontWeight: 600 }}>Politique de confidentialité</span>.
      </p>
    </AuthLayout>
  );
}
